import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaUserGraduate, 
  FaMoneyCheckAlt, 
  FaFileInvoice, 
  FaHistory,
  FaCalendarAlt,
  FaReceipt,
  FaEdit,
  FaSave,
  FaTimes
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import api from '../../../utils/api';
import useColleges from '../../../utils/useColleges';
import { useAuth } from '../../../context/AuthContext';
import './FeeCollectionLog.css';

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

const fmtDateTime = (d) => d
  ? new Date(d).toLocaleString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    })
  : '—';

const FeeCollectionLog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { colleges } = useColleges();
  const { allocation } = location.state || {};
  
  const [editingTxn, setEditingTxn] = useState(null);
  const [editAmount, setEditAmount] = useState('');
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);

  const receiptBasePath = user?.role === 'admin' ? '/admin'
    : user?.role === 'collegeadmin' ? '/collegeadmin'
    : '/clerk';

  if (!allocation) {
    return (
      <div className="Student-collect-fee-log-container">
        <div className="Student-collect-fee-log-empty">No fee allocation data found.</div>
      </div>
    );
  }

  const student      = allocation.student || {};
  const collegeName  = allocation.college?.name || allocation.college || '—';
  const transactions = allocation.transactions || [];

  const totalPaid    = transactions.reduce((s, t) => s + Number(t.paidAmount || 0), 0);
  const totalLateFee = Number(allocation.lateFee      || 0); // total ever charged
  const lateFeesPaid = Number(allocation.lateFeesPaid || 0); // total ever paid
  const lateFeeBalance = Math.max(0, totalLateFee - lateFeesPaid);

  const handleEditTransaction = (txn) => {
    setEditingTxn(txn._id);
    setEditAmount(txn.paidAmount.toString());
    setEditReason('');
  };

  const handleCancelEdit = () => {
    setEditingTxn(null);
    setEditAmount('');
    setEditReason('');
  };

  const handleSaveEdit = async (txn) => {
    const newAmount = Number(editAmount);
    const originalAmount = Number(txn.paidAmount);
    
    if (newAmount <= 0) {
      Swal.fire('Invalid Amount', 'Amount must be greater than 0', 'warning');
      return;
    }
    
    if (newAmount === originalAmount) {
      handleCancelEdit();
      return;
    }
    
    if (!editReason.trim()) {
      Swal.fire('Reason Required', 'Please provide a reason for changing the amount', 'warning');
      return;
    }
    
    setSaving(true);
    try {
      // amazonq-ignore-next-line
      await api.put(`/fees/transactions/${txn._id}`, {
        paidAmount: newAmount,
        reason: editReason.trim()
      });
      
      Swal.fire({
        title: 'Updated!',
        text: 'Transaction amount updated successfully',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
      
      // Refresh the page or update local state
      window.location.reload();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to update transaction', 'error');
    } finally {
      setSaving(false);
      handleCancelEdit();
    }
  };

  const handleViewReceipt = (txn) => {
    const items = (txn.items || []).map(i => ({ type: i.type, amountPaid: Number(i.amountPaid) || 0 }));
    const modes = txn.paymentModes && txn.paymentModes.length > 0
      ? txn.paymentModes
      : [{ mode: 'Cash', referenceNo: '—', amount: txn.paidAmount }];

    const balanceAfterTransaction = txn.balanceAfterTransaction !== undefined 
      ? txn.balanceAfterTransaction 
      : 0;

    // Use full college object from colleges array (has address, logo, titleHeader, etc.)
    const fullCollege = colleges.find(c => String(c._id) === String(allocation.college?._id || allocation.college)) || allocation.college || {};

    navigate(`${receiptBasePath}/fee-receipt`, {
      state: {
        allocation: {
          college:     fullCollege,
          student,
        },
        payment: {
          receiptNo:    txn.receiptNo || allocation.receiptNo || `RCP-${Date.now()}`,
          paidAt:       txn.paidAt,
          paidAmount:   Number(txn.paidAmount || 0),
          paymentModes: modes,
          items,
          // 🔥 FROZEN - Never recalculate, just use saved value
          balanceAfterTransaction,
          feeName:      txn.feeName || allocation.feeName || 'Fee',
          studentClass: txn.studentClass || allocation.class || 'N/A',
          sessionName:  txn.sessionName || allocation.session || 'N/A',
          itemsAtTransaction: txn.itemsAtTransaction || items,
        },
      }
    });
  };

  return (
    <div className="Student-collect-fee-log-container">

      {/* ── Header ── */}
      <div className="Student-collect-fee-log-header">
        <h2 className="Student-collect-fee-log-title">
          <FaHistory style={{ color: '#3b82f6' }} /> Fee Collection Log
        </h2>
        <button className="Student-collect-fee-log-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
      </div>

      {/* ── Info Cards ── */}
      <div className="Student-collect-fee-log-info-grid">
        {/* Student Card */}
        <div className="Student-collect-fee-log-card">
          <h3 className="Student-collect-fee-log-card-title">
            <FaUserGraduate style={{ color: '#64748b' }} /> Student Details
          </h3>
          <div className="Student-collect-fee-log-row">
            <span className="Student-collect-fee-log-label">Name</span>
            <span className="Student-collect-fee-log-value">{student.name || '—'}</span>
          </div>
          <div className="Student-collect-fee-log-row">
            <span className="Student-collect-fee-log-label">Admission No</span>
            <span className="Student-collect-fee-log-value">{student.admissionNo || '—'}</span>
          </div>
          <div className="Student-collect-fee-log-row">
            <span className="Student-collect-fee-log-label">Class</span>
            <span className="Student-collect-fee-log-value">{allocation.class || '—'}</span>
          </div>
          <div className="Student-collect-fee-log-row">
            <span className="Student-collect-fee-log-label">Batch (Year)</span>
            <span className="Student-collect-fee-log-value">{allocation.batch || allocation.session || '—'}</span>
          </div>
          <div className="Student-collect-fee-log-row">
            <span className="Student-collect-fee-log-label">College</span>
            <span className="Student-collect-fee-log-value">{collegeName}</span>
          </div>
        </div>

        {/* Fee Summary Card */}
        <div className="Student-collect-fee-log-card">
          <h3 className="Student-collect-fee-log-card-title">
            <FaMoneyCheckAlt style={{ color: '#64748b' }} /> Fee Summary
          </h3>
          <div className="Student-collect-fee-log-row">
            <span className="Student-collect-fee-log-label">Fee Structure</span>
            <span className="Student-collect-fee-log-value">{allocation.feeName || '—'}</span>
          </div>
          <div className="Student-collect-fee-log-row">
            <span className="Student-collect-fee-log-label">Total Fee Amount</span>
            <span className="Student-collect-fee-log-value">
              ₹ {Number(allocation.totalAmount || 0).toLocaleString()}
            </span>
          </div>
          <div className="Student-collect-fee-log-row">
            <span className="Student-collect-fee-log-label">Total Paid So Far</span>
            <span className="Student-collect-fee-log-value Student-collect-fee-log-text-green">
              ₹ {totalPaid.toLocaleString()}
            </span>
          </div>
          {totalLateFee > 0 && (
            <>
              <div className="Student-collect-fee-log-row">
                <span className="Student-collect-fee-log-label">Late Fee Charged</span>
                <span className="Student-collect-fee-log-value Student-collect-fee-log-text-red">
                  ₹ {totalLateFee.toLocaleString()}
                </span>
              </div>
              <div className="Student-collect-fee-log-row">
                <span className="Student-collect-fee-log-label">Late Fee Paid</span>
                <span className="Student-collect-fee-log-value Student-collect-fee-log-text-green">
                  ₹ {lateFeesPaid.toLocaleString()}
                </span>
              </div>
              {lateFeeBalance > 0 && (
                <div className="Student-collect-fee-log-row">
                  <span className="Student-collect-fee-log-label">Late Fee Remaining</span>
                  <span className="Student-collect-fee-log-value Student-collect-fee-log-text-red">
                    ₹ {lateFeeBalance.toLocaleString()}
                  </span>
                </div>
              )}
            </>
          )}
          <div className="Student-collect-fee-log-row">
            <span className="Student-collect-fee-log-label">Remaining Balance</span>
            <span className={`Student-collect-fee-log-value ${allocation.dueAmount > 0 ? 'Student-collect-fee-log-text-red' : 'Student-collect-fee-log-text-green'}`}>
              ₹ {Number(allocation.dueAmount || 0).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      {/* ── Transactions List ── */}
      <h3 className="Student-collect-fee-log-section-title">
        Payment Transactions ({transactions.length})
      </h3>

      {/* 🔥 Show late fee edits that happened without payment (zero-payment adjustments) */}
      {allocation.lateFeeEditHistoryWithoutTransaction && allocation.lateFeeEditHistoryWithoutTransaction.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 12px', color: '#ef4444', fontSize: '0.95rem', fontWeight: 700 }}>
            <FaEdit style={{ marginRight: '8px', color: '#ef4444' }} />
            Late Fee Adjustments (No Payment Made)
          </h4>
          {allocation.lateFeeEditHistoryWithoutTransaction.map((edit, i) => (
            <div key={i} className="Student-collect-fee-log-edit-entry" style={{ borderColor: '#fecaca', marginBottom: '12px' }}>
              <div className="Student-collect-fee-log-edit-meta">
                <span className="Student-collect-fee-log-edit-date">{fmtDateTime(edit.editedAt)}</span>
                <span className="Student-collect-fee-log-edit-by">
                  by {edit.editedBy || edit.editedByRole || 'Unknown'}
                  {edit.editedBy && edit.editedByRole && ` (${edit.editedByRole})`}
                </span>
              </div>
              <div className="Student-collect-fee-log-edit-change">
                <span className="Student-collect-fee-log-edit-old">₹{Number(edit.oldLateFee || 0).toLocaleString()}</span>
                <span className="Student-collect-fee-log-edit-arrow">→</span>
                <span className="Student-collect-fee-log-edit-new">₹{Number(edit.newLateFee || 0).toLocaleString()}</span>
                {edit.editType && (
                  <span style={{ 
                    marginLeft: '12px', 
                    padding: '2px 8px', 
                    borderRadius: '4px', 
                    fontSize: '0.75rem', 
                    fontWeight: 600,
                    background: edit.editType === 'waived' ? '#dcfce7' : '#fef3c7',
                    color: edit.editType === 'waived' ? '#047857' : '#d97706'
                  }}>
                    {edit.editType === 'waived' ? '✓ Waived' : 
                     edit.editType === 'reduced' ? '↓ Reduced' : 
                     edit.editType === 'increased' ? '↑ Increased' : edit.editType}
                  </span>
                )}
              </div>
              {edit.reason && (
                <div className="Student-collect-fee-log-edit-reason-text" style={{ background: '#fee2e2' }}>
                  <strong>Reason:</strong> {edit.reason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 🔥 Fix: Display the top-level edit history permanently regardless of transaction count */}
      {allocation.feeEditHistory && allocation.feeEditHistory.length > 0 && (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
          <h4 style={{ margin: '0 0 12px', color: '#475569', fontSize: '0.95rem', fontWeight: 700 }}>
            <FaEdit style={{ marginRight: '8px', color: '#64748b' }} />
            Fee Structure Base Adjustments
          </h4>
          {allocation.feeEditHistory.map((edit, i) => (
            <React.Fragment key={i}>
              {/* Particulars Added Section */}
              {edit.addedParticulars && edit.addedParticulars.length > 0 && (
                <div className="Student-collect-fee-log-edit-history-box" style={{ borderLeftColor: '#10b981', background: '#f0fdf4', marginBottom: '12px' }}>
                  <h4 className="Student-collect-fee-log-table-label" style={{ color: '#10b981', marginBottom: '8px' }}>
                    <FaEdit /> Particulars Added
                  </h4>
                  <div className="Student-collect-fee-log-edit-entry" style={{ borderColor: '#bbf7d0' }}>
                    <div className="Student-collect-fee-log-edit-meta">
                      <span className="Student-collect-fee-log-edit-date">{fmtDateTime(edit.editedAt)}</span>
                      <span className="Student-collect-fee-log-edit-by">
                        by {edit.editedBy || edit.editedByRole || 'Unknown'}
                        {edit.editedBy && edit.editedByRole && ` (${edit.editedByRole})`}
                      </span>
                    </div>
                    <div className="Student-collect-fee-log-edit-change" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                      {edit.addedParticulars.map((particular, idx) => (
                        <div key={idx} style={{ fontSize: '0.9rem', color: '#047857', fontWeight: 600 }}>
                          ✓ {particular}
                        </div>
                      ))}
                    </div>
                    {edit.reason && (
                      <div className="Student-collect-fee-log-edit-reason-text" style={{ background: '#dcfce7' }}>
                        <strong>Reason:</strong> {edit.reason}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Amounts Edited Section */}
              {edit.editedParticulars && edit.editedParticulars.length > 0 && (
                <div className="Student-collect-fee-log-edit-history-box" style={{ borderLeftColor: '#3b82f6', background: '#eff6ff', marginBottom: '12px' }}>
                  <h4 className="Student-collect-fee-log-table-label" style={{ color: '#3b82f6', marginBottom: '8px' }}>
                    <FaEdit /> Fee Amounts Edited
                  </h4>
                  <div className="Student-collect-fee-log-edit-entry" style={{ borderColor: '#bfdbfe' }}>
                    <div className="Student-collect-fee-log-edit-meta">
                      <span className="Student-collect-fee-log-edit-date">{fmtDateTime(edit.editedAt)}</span>
                      <span className="Student-collect-fee-log-edit-by">
                        by {edit.editedBy || edit.editedByRole || 'Unknown'}
                        {edit.editedBy && edit.editedByRole && ` (${edit.editedByRole})`}
                      </span>
                    </div>
                    <div className="Student-collect-fee-log-edit-change" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                      {edit.editedParticulars.map((particular, idx) => (
                        <div key={idx} style={{ fontSize: '0.9rem', color: '#1e40af', fontWeight: 600 }}>
                          {particular}
                        </div>
                      ))}
                    </div>
                    {edit.reason && (
                      <div className="Student-collect-fee-log-edit-reason-text" style={{ background: '#dbeafe' }}>
                        <strong>Reason:</strong> {edit.reason}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      {/* 🔥 Fix: Decoupled empty state check so pre-payment base edits can render at the top without being hidden */}
      {transactions.length === 0 ? (
        <div className="Student-collect-fee-log-empty" style={{ margin: '0 0 24px 0' }}>
          No payment transactions have been recorded yet.
        </div>
      ) : (
        <div className="Student-collect-fee-log-txn-list">
          {transactions.map((txn, idx) => {
            const modes = txn.paymentModes && txn.paymentModes.length > 0
              ? txn.paymentModes
              : [{ mode: 'Cash', referenceNo: '—', amount: txn.paidAmount }];

            return (
              <div className="Student-collect-fee-log-txn-card" key={txn._id || idx}>
                
                {/* Transaction Header */}
                <div className="Student-collect-fee-log-txn-head">
                  <div className="Student-collect-fee-log-txn-head-left">
                    <span className="Student-collect-fee-log-txn-num">#{idx + 1}</span>
                    <span className="Student-collect-fee-log-txn-date">
                      <FaCalendarAlt /> {fmtDate(txn.paidAt)}
                    </span>
                    {txn.receiptNo && (
                      <span className="Student-collect-fee-log-txn-rcpt">
                        <FaReceipt /> {txn.receiptNo}
                      </span>
                    )}
                  </div>
                  <div className="Student-collect-fee-log-txn-head-right">
                    {editingTxn === txn._id ? (
                      <div className="Student-collect-fee-log-edit-section">
                        <input
                          type="text"
                          inputMode="numeric"
                          value={editAmount}
                          onChange={(e) => {
                            const val = e.target.value;
                            // Only allow numbers and decimal point
                            if (val === '' || /^\d*\.?\d*$/.test(val)) {
                              setEditAmount(val);
                            }
                          }}
                          className="Student-collect-fee-log-edit-amount"
                          placeholder="Amount"
                        />
                        <input
                          type="text"
                          value={editReason}
                          onChange={(e) => setEditReason(e.target.value)}
                          placeholder="Reason for change (required)"
                          className="Student-collect-fee-log-edit-reason"
                        />
                        <button 
                          className="Student-collect-fee-log-save-btn" 
                          onClick={() => handleSaveEdit(txn)}
                          disabled={saving}
                        >
                          <FaSave /> {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button 
                          className="Student-collect-fee-log-cancel-btn" 
                          onClick={handleCancelEdit}
                          disabled={saving}
                        >
                          <FaTimes /> Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <span className="Student-collect-fee-log-txn-amount">
                          ₹ {Number(txn.paidAmount || 0).toLocaleString()}
                        </span>

                        <button 
                          className="Student-collect-fee-log-receipt-btn" 
                          onClick={() => handleViewReceipt(txn)}
                        >
                          <FaFileInvoice /> View Receipt
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Transaction Body (Tables) */}
                <div className="Student-collect-fee-log-txn-body">
                  
                  {/* Particulars Table */}
                  <div className="Student-collect-fee-log-txn-section">
                    <h4 className="Student-collect-fee-log-table-label">Fee Particulars</h4>
                    <table className="Student-collect-fee-log-sub-table">
                      <thead>
                        <tr>
                          <th>Particular Name</th>
                          <th className="Student-collect-fee-log-align-right">Amount Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {txn.items && txn.items.length > 0 ? (
                          txn.items.map((item, i) => (
                            <tr key={i}>
                              <td style={{ fontWeight: 600 }}>{item.type || '—'}</td>
                              <td className="Student-collect-fee-log-align-right Student-collect-fee-log-text-green" style={{ fontWeight: 700 }}>
                                ₹ {Number(item.amountPaid || 0).toLocaleString()}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td style={{ fontWeight: 600 }}>{allocation.feeName || 'Fee Payment'}</td>
                            <td className="Student-collect-fee-log-align-right Student-collect-fee-log-text-green" style={{ fontWeight: 700 }}>
                              ₹ {Number(txn.paidAmount || 0).toLocaleString()}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Payment Modes Table */}
                  <div className="Student-collect-fee-log-txn-section">
                    <h4 className="Student-collect-fee-log-table-label">Payment Modes</h4>
                    <table className="Student-collect-fee-log-sub-table">
                      <thead>
                        <tr>
                          <th>Mode</th>
                          <th>Date</th>
                          <th>Reference Number</th>
                          <th className="Student-collect-fee-log-align-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {modes.map((m, i) => (
                          <tr key={i}>
                            <td>
                              <span className="Student-collect-fee-log-mode-badge">{m.mode || 'Cash'}</span>
                            </td>
                            <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                              {m.date ? fmtDate(m.date) : fmtDate(txn.paidAt)}
                            </td>
                            <td style={{ color: '#64748b', fontSize: '0.85rem' }}>
                              {m.referenceNo || '—'}
                            </td>
                            <td className="Student-collect-fee-log-align-right" style={{ fontWeight: 700 }}>
                              ₹ {Number(m.amount || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Narration */}
                  {txn.narration && (
                    <div className="Student-collect-fee-log-narration-box">
                      <p className="Student-collect-fee-log-narration-text">
                        <strong>Remarks:</strong> {txn.narration}
                      </p>
                    </div>
                  )}

                  {/* Edit History - Show if amount was edited */}
                  {txn.editHistory && txn.editHistory.length > 0 && (
                    <div className="Student-collect-fee-log-edit-history-box">
                      <h4 className="Student-collect-fee-log-table-label" style={{ color: '#f59e0b', marginBottom: '8px' }}>
                        <FaEdit /> Transaction Amount Edit History
                      </h4>
                      {txn.editHistory.map((edit, i) => (
                        <div key={i} className="Student-collect-fee-log-edit-entry">
                          <div className="Student-collect-fee-log-edit-meta">
                            <span className="Student-collect-fee-log-edit-date">{fmtDateTime(edit.editedAt)}</span>
                            <span className="Student-collect-fee-log-edit-by">
                              by {edit.editedBy || edit.editedByRole || 'Unknown'}
                              {edit.editedBy && edit.editedByRole && ` (${edit.editedByRole})`}
                            </span>
                          </div>
                          <div className="Student-collect-fee-log-edit-change">
                            <span className="Student-collect-fee-log-edit-old">₹{Number(edit.oldAmount || 0).toLocaleString()}</span>
                            <span className="Student-collect-fee-log-edit-arrow">→</span>
                            <span className="Student-collect-fee-log-edit-new">₹{Number(edit.newAmount || 0).toLocaleString()}</span>
                          </div>
                          {edit.reason && (
                            <div className="Student-collect-fee-log-edit-reason-text">
                              <strong>Reason:</strong> {edit.reason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Late Fee Edit History - Show if late fee was edited */}
                  {txn.lateFeeEditHistory && txn.lateFeeEditHistory.length > 0 && (
                    <div className="Student-collect-fee-log-edit-history-box" style={{ borderLeftColor: '#ef4444', background: '#fef2f2' }}>
                      <h4 className="Student-collect-fee-log-table-label" style={{ color: '#ef4444', marginBottom: '8px' }}>
                        <FaEdit /> Late Fee Edit History
                      </h4>
                      {txn.lateFeeEditHistory.map((edit, i) => (
                        <div key={i} className="Student-collect-fee-log-edit-entry" style={{ borderColor: '#fecaca' }}>
                          <div className="Student-collect-fee-log-edit-meta">
                            <span className="Student-collect-fee-log-edit-date">{fmtDateTime(edit.editedAt)}</span>
                            <span className="Student-collect-fee-log-edit-by">
                              by {edit.editedBy || edit.editedByRole || 'Unknown'}
                              {edit.editedBy && edit.editedByRole && ` (${edit.editedByRole})`}
                            </span>
                          </div>
                          <div className="Student-collect-fee-log-edit-change">
                            <span className="Student-collect-fee-log-edit-old">₹{Number(edit.oldLateFee || 0).toLocaleString()}</span>
                            <span className="Student-collect-fee-log-edit-arrow">→</span>
                            <span className="Student-collect-fee-log-edit-new">₹{Number(edit.newLateFee || 0).toLocaleString()}</span>
                          </div>
                          {edit.reason && (
                            <div className="Student-collect-fee-log-edit-reason-text" style={{ background: '#fee2e2' }}>
                              <strong>Reason:</strong> {edit.reason}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Fee Structure Edit History - Show if fee amounts/particulars were edited */}
                  {txn.feeEditHistory && txn.feeEditHistory.length > 0 && (
                    <>
                      {txn.feeEditHistory.map((edit, i) => (
                        <React.Fragment key={i}>
                          {/* Particulars Added Section - Green */}
                          {edit.addedParticulars && edit.addedParticulars.length > 0 && (
                            <div className="Student-collect-fee-log-edit-history-box" style={{ borderLeftColor: '#10b981', background: '#f0fdf4', marginBottom: '12px' }}>
                              <h4 className="Student-collect-fee-log-table-label" style={{ color: '#10b981', marginBottom: '8px' }}>
                                <FaEdit /> Particulars Added
                              </h4>
                              <div className="Student-collect-fee-log-edit-entry" style={{ borderColor: '#bbf7d0' }}>
                                <div className="Student-collect-fee-log-edit-meta">
                                  <span className="Student-collect-fee-log-edit-date">{fmtDateTime(edit.editedAt)}</span>
                                  <span className="Student-collect-fee-log-edit-by">
                                    by {edit.editedBy || edit.editedByRole || 'Unknown'}
                                    {edit.editedBy && edit.editedByRole && ` (${edit.editedByRole})`}
                                  </span>
                                </div>
                                <div className="Student-collect-fee-log-edit-change" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                  {edit.addedParticulars.map((particular, idx) => (
                                    <div key={idx} style={{ fontSize: '0.9rem', color: '#047857', fontWeight: 600 }}>
                                      ✓ {particular}
                                    </div>
                                  ))}
                                </div>
                                {edit.reason && (
                                  <div className="Student-collect-fee-log-edit-reason-text" style={{ background: '#dcfce7' }}>
                                    <strong>Reason:</strong> {edit.reason}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Amounts Edited Section - Blue */}
                          {edit.editedParticulars && edit.editedParticulars.length > 0 && (
                            <div className="Student-collect-fee-log-edit-history-box" style={{ borderLeftColor: '#3b82f6', background: '#eff6ff', marginBottom: '12px' }}>
                              <h4 className="Student-collect-fee-log-table-label" style={{ color: '#3b82f6', marginBottom: '8px' }}>
                                <FaEdit /> Fee Amounts Edited
                              </h4>
                              <div className="Student-collect-fee-log-edit-entry" style={{ borderColor: '#bfdbfe' }}>
                                <div className="Student-collect-fee-log-edit-meta">
                                  <span className="Student-collect-fee-log-edit-date">{fmtDateTime(edit.editedAt)}</span>
                                  <span className="Student-collect-fee-log-edit-by">
                                    by {edit.editedBy || edit.editedByRole || 'Unknown'}
                                    {edit.editedBy && edit.editedByRole && ` (${edit.editedByRole})`}
                                  </span>
                                </div>
                                <div className="Student-collect-fee-log-edit-change" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                  {edit.editedParticulars.map((particular, idx) => (
                                    <div key={idx} style={{ fontSize: '0.9rem', color: '#1e40af', fontWeight: 600 }}>
                                      {particular}
                                    </div>
                                  ))}
                                </div>
                                {edit.reason && (
                                  <div className="Student-collect-fee-log-edit-reason-text" style={{ background: '#dbeafe' }}>
                                    <strong>Reason:</strong> {edit.reason}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </>
                  )}

                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Footer Summary ── */}
      <div className="Student-collect-fee-log-summary">
        <div className="Student-collect-fee-log-summary-row">
          <span className="Student-collect-fee-log-summary-label">Total Fee Amount</span>
          <span className="Student-collect-fee-log-summary-value">₹ {Number(allocation.totalAmount || 0).toLocaleString()}</span>
        </div>
        <div className="Student-collect-fee-log-summary-row">
          <span className="Student-collect-fee-log-summary-label">Total Paid ({transactions.length} transaction{transactions.length !== 1 ? 's' : ''})</span>
          <span className="Student-collect-fee-log-summary-value Student-collect-fee-log-text-green">₹ {totalPaid.toLocaleString()}</span>
        </div>
        {totalLateFee > 0 && (
          <div className="Student-collect-fee-log-summary-row">
            <span className="Student-collect-fee-log-summary-label">Late Fee: ₹{totalLateFee.toLocaleString()} charged &mdash; ₹{lateFeesPaid.toLocaleString()} paid</span>
            <span className={`Student-collect-fee-log-summary-value ${lateFeeBalance > 0 ? 'Student-collect-fee-log-text-red' : 'Student-collect-fee-log-text-green'}`}>
              {lateFeeBalance > 0 ? `₹ ${lateFeeBalance.toLocaleString()} remaining` : '✓ Cleared'}
            </span>
          </div>
        )}
        <div className="Student-collect-fee-log-divider"></div>
        <div className="Student-collect-fee-log-summary-row Student-collect-fee-log-summary-total">
          <span className="Student-collect-fee-log-summary-label">Remaining Balance</span>
          <span className={`Student-collect-fee-log-summary-value ${allocation.dueAmount > 0 ? 'Student-collect-fee-log-text-red' : 'Student-collect-fee-log-text-green'}`}>
            ₹ {Number(allocation.dueAmount || 0).toLocaleString()}
          </span>
        </div>
      </div>

    </div>
  );
};

export default FeeCollectionLog;